import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useInfiniteScroll } from "ahooks";
import { X } from "lucide-react";
import { ChatFramework } from "@/components/layout";
import { Typography } from "@/components/fortress";
import { PreviewImage } from "@/components/ui/image";
import { PrivacyPolicy } from "@/pages/components/PrivacyPolicy";
import { Message } from "@/pages/chat/components/Message";
import { InputBar } from "@/pages/chat/components/InputBar";
import { Login } from "@/pages/chat/components/Login";
import { CSAT } from "@/pages/chat/components/CSAT";
import { WhatsApp } from "@/pages/chat/components/WhatsApp";
import { GladlyApp } from "@/pages/chat/components/GladlyApp";
import { Bot, Failed } from "@/components/icons";
import { useChatStore } from "@/stores/chatStore";
import type { Conversation, MessageData, File } from "@/types";
import {
  ConversationStatusEnum,
  FileStatus,
  MessageStatusEnum,
  HandoffActionEnum,
  Market,
} from "@/types";
import { cn, messageDateFormatter } from "@/lib/utils";
import { LOGIN_API, CONVERSATION_API, CHAT_API } from "@/config/api";
import type { APIResponse } from "@/utils/apiHelper";
import {
  apiGet,
  apiPost,
  getAccessToken,
  getCachedDeviceInfo,
  throwCasaApiError,
} from "@/utils";
import {
  EMPTY_CONVERSATION_ID,
  FeedbackSentence,
  isEmptyConversation,
} from "@/constant";
import {
  generateUserMessage,
  generateAiMessage,
  generateConversation,
} from "@/utils/message";
import { useToast } from "@/hooks/useToast";
import { fetchChatHistory } from "@/service";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  SUBMIT_ACTION,
  trackEvent,
} from "@/utils/analytics";
import { servicesConfig } from "@/config";
import { motion } from "framer-motion";
import {
  GLADLY_REFRESH_MESSAGE,
  handoffToGladly,
  shouldPromptGladlyRefresh,
} from "@/modules/casa/utils/chatMode";
import { ChatProvider } from "./ChatContext";
import { MessageChangeEvent } from "@/lib/message";
import { DislikeReason } from "./components/DislikeReason";

const PageSize = 20;

enum LoadingStatus {
  THINKING,
  GATHERING_INFO,
}

const loadingChangeInterval = 5000;
const LoadingMessage = () => {
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(
    LoadingStatus.THINKING,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingStatus(LoadingStatus.GATHERING_INFO);
    }, loadingChangeInterval);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex justify-start">
      <div className="flex flex-col">
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0 flex items-center justify-center w-5 h-5 text-xs text-white rounded-full bg-[#844025]">
            <div className="w-3 h-3">
              <Bot />
            </div>
          </div>
          <div className="text-[14px] text-[#844025]">Casa</div>
        </div>
        <div className="mt-[6px] px-[15px] py-[10px] w-fit rounded-md rounded-tl-none text-[14px] border border-[#F6F3E7] bg-[#FBF9F4]">
          {loadingStatus === LoadingStatus.THINKING && (
            <div className="relative inline-flex items-center pl-5">
              <div className="flex-shrink-0 absolute top-1/2 -translate-y-1/2 left-0 w-4 h-4">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#BDA6AE] z-2 animate-breath-1.2s"></div>
                <div className="absolute top-0 left-0 w-full h-full rounded-full ripple-gradient-1 opacity-0 animate-ripple-2.4s"></div>
                <div className="absolute top-0 left-0 w-full h-full rounded-full ripple-gradient-2 opacity-0 animate-ripple-2.4s-delay"></div>
              </div>
              <span className="flex-shrink-0 mr-[2px] text-[#987983]">
                Thinking
              </span>
              {/* <motion.span
                initial={{ maxWidth: 0 }}
                animate={{ maxWidth: "100%" }}
                transition={{
                  duration: 1.5,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
                className="inline-block whitespace-nowrap overflow-hidden opacity-80 animate-pulse bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #AF939D 0%, #5F3844 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                ...
              </motion.span> */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
                  width: "40%",
                }}
                initial={{ x: "-50%" }}
                animate={{ x: "200%" }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: [0.4, 0, 0.6, 1],
                }}
              />
            </div>
          )}

          {loadingStatus === LoadingStatus.GATHERING_INFO && (
            <div className="relative inline-flex items-center pl-5">
              <div className="flex-shrink-0 absolute top-1/2 -translate-y-1/2 left-0 w-4 h-4">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#BDA6AE] z-2 animate-breath-1.2s"></div>
                <div className="absolute top-0 left-0 w-full h-full rounded-full ripple-gradient-1 opacity-0 animate-ripple-2.4s"></div>
                <div className="absolute top-0 left-0 w-full h-full rounded-full ripple-gradient-2 opacity-0 animate-ripple-2.4s-delay"></div>
              </div>
              <span className="flex-shrink-0 mr-[2px] text-[#987983]">
                Gathering information
              </span>
              {/* <motion.span
                initial={{ maxWidth: 0 }}
                animate={{ maxWidth: "100%" }}
                transition={{
                  duration: 1.5,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
                className="inline-block whitespace-nowrap overflow-hidden opacity-80 animate-pulse bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #AF939D 0%, #5F3844 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                ...
              </motion.span> */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
                  width: "20%",
                }}
                initial={{ x: "-50%" }}
                animate={{ x: "450%" }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: [0.4, 0, 0.6, 1],
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LoadingMoreHistory = () => {
  return (
    <div className="flex justify-center py-6">
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-[#844025] rounded-full animate-spin"></div>
        <span>Loading more history...</span>
      </div>
    </div>
  );
};

const UserTip = () => {
  return (
    <div className="flex flex-col mb-6">
      <PrivacyPolicy />
    </div>
  );
};

const UploadedFile = ({
  file,
  onDelete,
}: {
  file: File;
  onDelete: (id: string) => void;
}) => {
  return (
    <div className={cn("relative w-[60px] h-[60px] ")}>
      {/* image */}
      {file.status !== FileStatus.FAILED && (
        <div className="w-full h-full border border-[#DADADA] rounded-md overflow-hidden">
          <PreviewImage src={file.localUrl || file.url} alt={file.name} />
        </div>
      )}

      {/* failed */}
      {file.status === FileStatus.FAILED && (
        <div className="w-full h-full flex flex-col items-center justify-center border border-primary rounded-md">
          <Failed />
          <Typography level="caption2" className="text-[#84333C]">
            Failed
          </Typography>
        </div>
      )}

      {/* close icon */}
      <div
        className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#757575] flex items-center justify-center cursor-pointer"
        onClick={() => onDelete(file.id)}
      >
        <X className="w-3 h-3 text-white" />
      </div>
    </div>
  );
};

const NiceToMeetYou = ({
  onSendMessage,
}: {
  onSendMessage: (message: string, isFromOption?: boolean) => void;
}) => {
  return (
    <div className="flex flex-col space-y-3 px-2 pb-2">
      <Typography
        level="h3"
        className="text-foreground"
        sx={() => ({
          fontSize: "32px !important",
        })}
      >
        <div className="flex items-center space-x-3">
          <Bot fill="#844025" />
          <span>Nice to meet you!</span>
        </div>
      </Typography>
      <div className="flex flex-col space-y-2">
        <Typography level="caption1" style={{ wordBreak: "break-word" }}>
          I'm Casa, your shopping assistant. I can help you find deals, track
          orders, compare furniture pieces, and more.
        </Typography>
        <Typography
          className="text-[#7C5F68]"
          level="caption1"
          style={{ wordBreak: "break-word" }}
        >
          Quick reminder: I'm an AI so do double-check important details.
        </Typography>
      </div>
      <div
        className="border border-[#3C101E] rounded-[6px] py-2 px-4 w-fit cursor-pointer"
        onClick={() => onSendMessage("Show me current deals")}
      >
        <Typography level="caption1">Show me current deals</Typography>
      </div>
      <div
        className="border border-[#3C101E] rounded-[6px] py-2 px-4 w-fit cursor-pointer"
        onClick={() => onSendMessage("Help me choose the right sofa")}
      >
        <Typography level="caption1">Help me choose the right sofa</Typography>
      </div>
      <div
        className="border border-[#3C101E] rounded-[6px] py-2 px-4 w-fit cursor-pointer"
        onClick={() => onSendMessage("Show me bestsellers")}
      >
        <Typography level="caption1">Show me bestsellers</Typography>
      </div>
    </div>
  );
};

type ChatPageInitContext = {
  conversationId: number;
};

const ConversationDivider = ({ info }: { info: string }) => {
  return (
    <div className="flex items-center my-[24px] mb-4 mx-8">
      <div className="flex-grow border-t border-gray-300" />
      <span className="mx-2 text-[11px] text-gray-500">{info}</span>
      <div className="flex-grow border-t border-gray-300" />
    </div>
  );
};

function isTurnOffConversation(conversationStatus: ConversationStatusEnum) {
  return (
    conversationStatus === ConversationStatusEnum.CLOSED ||
    conversationStatus === ConversationStatusEnum.HANDOFF
  );
}

function isTurnOnConversation(conversationStatus: ConversationStatusEnum) {
  return (
    conversationStatus === ConversationStatusEnum.ACTIVE ||
    conversationStatus === ConversationStatusEnum.PENDING
  );
}

import { registerAnalyticsContextProvider } from "@/utils/analytics";

/**
 * Chat Page 组件的整体工作逻辑：
 * 1. 初始化时，使用 useInfiniteScroll 拉取历史消息，按时间倒序排列，此时 ChatPage 的 firstLoading 状态为 false
 * 2. firstLoading 为 false 后，则完成 history 的拉取，
 *    接下来检查 history 的最后一条消息对应的 conversation 更新 currentConversation State
 * 3. 根据 chat history 对 conversation 进行判断：
 *  - 如果没有 conversation，则发起空对话请求 AI 返回欢迎语
 *  - 如果有 conversation
 *    - 如果 conversation 状态为非 pending（包括 active、closed、handoff），则进入正常轮询流程
 *    - 如果 conversation 状态为 pending，标记对话框进入 loading 等待返回，
 *      则进入正常轮询流程，并关注 conversation 状态变化，一旦转为非 pending，拉取最新消息并展示，结束 loading 状态
 *  - 完成以上检查后，标记 isCheckedConversation 为 true
 * 4. 进入轮询流程，标记 isPolling 为 true，定时（例如每 1 秒）拉取最新对话状态
 * 5. 退出 ChatPage，上述所有状态均重置
 */
export const ChatPage: React.FC = () => {
  const {
    market,
    version,
    entryPath,
    hasReviewed,
    isBusinessHours,
    htmlUrl,
    handoffAction,
    setCurrentView,
    closeChat,
    setLoginInProgress,
    setHasReviewed,
    setIsBusinessHours,
    setHtmlUrl,
    setHandoffAction,
    files,
    clearFiles,
    delFile,
    updateLastMessageTimestamp,
    removeHadUnReadMessage,
    lastMessageTimestamp,
  } = useChatStore();

  const { toast } = useToast();

  /**
   * 初始化完成后，需要对 history 的最后一个对话和消息进行检查
   * 如果 conversation 状态为 pending，记录 conversation context
   * 当轮询到 conversation 状态变化时进行处理
   */
  const [isCheckedConversation, setIsCheckedConversation] = useState(false);
  const [initContext, setInitContext] = useState<ChatPageInitContext | null>(
    null,
  );
  const [isPolling, setIsPolling] = useState(false);
  const [currentConversation, setCurrentConversation] =
    useState<Conversation | null>(null);
  const currentConversationRef = useRef(currentConversation);

  useEffect(() => {
    currentConversationRef.current = currentConversation;
  }, [currentConversation]);

  useEffect(() => {
    return registerAnalyticsContextProvider(() => {
      const conversationId = currentConversationRef.current?.conversation_id;
      const conversationStatus =
        currentConversationRef.current?.conversation_status;
      return {
        conversationId:
          conversationId && conversationId !== 0 ? conversationId : undefined,
        conversationStatus: conversationStatus || undefined,
      };
    });
  }, []);

  const [isMessageLoading, setIsMessageLoading] = useState(false);
  const [loginVisible, setLoginVisible] = useState(false);
  const [CSATVisible, setCSATVisible] = useState(false);
  const [dislikeReasonPanelInfo, setDislikeReasonPanelInfo] = useState<{
    visible: boolean;
    messageId: string | null;
  }>({
    visible: false,
    messageId: null,
  });
  const [whatsAppVisible, setWhatsAppVisible] = useState<{
    source: "conversation" | "message" | null;
    visible: boolean;
  }>({
    source: null,
    visible: false,
  });
  const [gladlyVisible, setGladlyVisible] = useState<{
    source: "conversation" | "message" | null;
    visible: boolean;
  }>({
    source: null,
    visible: false,
  });
  const [isInputEmpty, setIsInputEmpty] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageId = useRef<string | null>(null);
  const chatFrameworkRef = useRef<HTMLDivElement>(null);
  const chatFrameworkContentRef = useRef<HTMLDivElement>(null);

  const isLoginOptionalPending = useRef(false);
  const [currentPage, setCurrentPage] = useState(-1);
  const [messageTotal, setMessageTotal] = useState(0);

  const {
    data: messageData,
    loading: firstLoading,
    loadingMore,
    error: dataLoadingError,
    mutate,
  } = useInfiniteScroll(
    async () => {
      const newCurrentPage = currentPage + 1;
      setCurrentPage(newCurrentPage);
      const { total, list, is_optional_login_pending } = await fetchChatHistory(
        {
          page: newCurrentPage,
          pageSize: PageSize,
        },
      );
      setMessageTotal(total);
      // DANGER: 这个字段需要优化一下
      isLoginOptionalPending.current = is_optional_login_pending;
      return {
        list,
        total,
      };
    },
    {
      target: chatFrameworkContentRef,
      direction: "top",
      threshold: 100, // 距离顶部100px时触发加载
      isNoMore: (data): boolean => {
        if (data?.list?.length) {
          return data.list.length >= messageTotal;
        }
        return true;
      },
      onError(error) {
        // tested!
        throwCasaApiError(error, "[ChatPage] Failed to fetch chat history");
      },
      onSuccess(data) {
        if (currentConversation === null) {
          const currentMessage = data.list[data.list.length - 1];
          if (currentMessage) {
            const conversation = {
              conversation_id: currentMessage.conversation_id,
              conversation_status: currentMessage.conversation_status,
            };
            setCurrentConversation(conversation);
          }
        }
      },
    },
  );

  const modifyMessageData = useCallback(
    (
      updater: (
        prevData: { list: MessageData[]; total: number } | undefined,
      ) => { list: MessageData[]; total: number },
    ) => {
      mutate((prevData) => {
        const newData = updater(prevData);
        const collectedIds: string[] = [];
        const newList = [];
        for (let i = newData.list.length - 1; i >= 0; i--) {
          const msg = newData.list[i];
          if (collectedIds.indexOf(msg.message_id) === -1) {
            collectedIds.push(msg.message_id);
            newList.unshift(msg);
          }
        }
        return {
          list: newList,
          total: newData.total,
        };
      });
    },
    [mutate],
  );

  const handleMessageIsLikeChange = async (
    changeEvent: MessageChangeEvent,
  ): Promise<boolean> => {
    const changes = changeEvent.changes();

    if (changes.is_like === undefined) {
      return true;
    }

    const isCancelLike = changes.is_like.newValue === null;

    if (isCancelLike) {
      const response = await apiPost(CHAT_API.messageReviewCancel(), {
        message_id: changeEvent.messageId,
      });

      if (response.code !== 0) {
        toast({
          description: `Cancel like/dislike failed: ${response.msg}`,
        });
        return false;
      }
    } else {
      const response = await apiPost(CHAT_API.messageReview(), {
        message_id: changeEvent.messageId,
        is_like: changes.is_like.newValue,
      });

      if (response.code !== 0) {
        toast({
          description: `Like/dislike failed: ${response.msg}`,
        });
        return false;
      }
    }

    const isClickDislike = changes.is_like.newValue === false;
    const isClickLike = changes.is_like.newValue === true;

    if (isClickDislike) {
      setDislikeReasonPanelInfo({
        visible: true,
        messageId: changeEvent.messageId,
      });
    } else if (isClickLike || isCancelLike) {
      toast({
        description: FeedbackSentence,
      });
    }

    return true;
  };

  const onBeforeApplyMessageChange = async (
    changeEvent: MessageChangeEvent,
  ): Promise<boolean> => {
    let isValid = true;
    isValid = await handleMessageIsLikeChange(changeEvent);

    return isValid;
  };

  const onMessageChange = useCallback(
    async (messageChangeEvent: MessageChangeEvent) => {
      const isValid = await onBeforeApplyMessageChange(messageChangeEvent);
      if (!isValid) {
        return;
      }

      modifyMessageData((prevData) => {
        if (!prevData) {
          return { list: [], total: 0 };
        }

        const len = prevData.list.length;
        const newData = prevData;

        for (let i = 0; i < len; i++) {
          const msg = prevData.list[i];
          if (msg.message_id === messageChangeEvent.messageId) {
            newData.list[i] = messageChangeEvent.newMessage();
            break;
          }
        }

        return newData;
      });
    },
    [modifyMessageData],
  );

  const handleConversationContextCheck = useCallback(() => {
    if (
      currentConversation &&
      currentConversation.conversation_status === ConversationStatusEnum.PENDING
    ) {
      setIsMessageLoading(true);
      setInitContext({
        conversationId: currentConversation.conversation_id,
      });
    }
    setIsCheckedConversation(true);
  }, [currentConversation]);

  const fetchLatestMessageForPendingConversation = async () => {
    try {
      const { list, total } = await fetchChatHistory({
        page: 0,
        pageSize: PageSize,
      });
      const latestMessage = list[list.length - 1];
      modifyMessageData((prevData) => {
        const list = prevData?.list || [];
        const newMessages = [...list, latestMessage];
        return {
          list: newMessages,
          total: total,
        };
      });
    } catch (error) {
      console.error("Error fetching latest message:", error);
      throwCasaApiError(error, "[ChatPage] Failed to fetch latest message");
    } finally {
      setIsMessageLoading(false);
    }
  };

  useEffect(() => {
    if (!firstLoading) {
      // first loading closed, then check conversation context
      handleConversationContextCheck();
      // remove unread message badge if any
      removeHadUnReadMessage();
    }
  }, [firstLoading]);

  useEffect(() => {
    const lastMessage = messageData?.list[messageData.list.length - 1];

    if (lastMessage) {
      const lastTimestamp = new Date(lastMessage.created_at).getTime();
      // if lastMessageTimestamp is null or lastTimestamp is greater than lastMessageTimestamp, update it
      if (
        lastMessageTimestamp === null ||
        lastTimestamp > lastMessageTimestamp
      ) {
        updateLastMessageTimestamp(lastTimestamp);
      }
    }

    if (lastMessage?.message_id !== lastMessageId.current) {
      lastMessageId.current = lastMessage?.message_id || null;
      setTimeout(() => {
        // 使用 scrollTop 直接控制内容区域的滚动，而不是 scrollIntoView
        if (messagesEndRef.current && chatFrameworkContentRef.current) {
          const scrollContainer = chatFrameworkContentRef.current;
          const targetElement = messagesEndRef.current;
          const scrollTop = targetElement.offsetTop - scrollContainer.offsetTop;
          scrollContainer.scrollTo({
            top: scrollTop,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [messageData?.list?.length]);

  const rawConversations = useMemo(() => {
    const result: (Conversation & { messages: MessageData[] })[] = [];
    let currentConversationId: number | null = null;
    let currentMessages: MessageData[] = [];
    let pendingMessages: MessageData[] = [];

    messageData?.list.forEach((message) => {
      if (isEmptyConversation(message.conversation_id)) {
        pendingMessages.push(message);
      } else {
        if (message.conversation_id !== currentConversationId) {
          currentConversationId = message.conversation_id;
          currentMessages = [message];

          result.push({
            conversation_id: currentConversationId,
            conversation_status:
              message.conversation_status || ConversationStatusEnum.ACTIVE,
            messages: currentMessages,
          });
        } else {
          currentMessages.push(message);
        }
      }
    });

    if (pendingMessages.length > 0) {
      result.push({
        conversation_id: EMPTY_CONVERSATION_ID,
        conversation_status: ConversationStatusEnum.ACTIVE,
        messages: pendingMessages,
      });
    }

    return result;
  }, [messageData]);

  const conversationList = useMemo(() => {
    if (
      currentConversation &&
      !isEmptyConversation(currentConversation.conversation_id)
    ) {
      return rawConversations.map((conversation) => {
        if (
          conversation.conversation_id === currentConversation.conversation_id
        ) {
          return {
            ...conversation,
            conversation_status: currentConversation.conversation_status,
          };
        }
        return conversation;
      });
    }
    return rawConversations;
  }, [
    rawConversations,
    currentConversation?.conversation_id,
    currentConversation?.conversation_status,
  ]);

  const handleLogin = () => {
    if (market === null) {
      return;
    }

    // 设置登录流程标记
    setLoginInProgress(true);

    // 关闭 ChatPage
    closeChat();

    const currentUrl = window.location.href;
    const marketLowerCase = market.toLowerCase();
    const hostname = window.location.hostname;
    window.location.href = `https://${hostname}/${marketLowerCase}/login?${new URLSearchParams(
      {
        redirectUrl: currentUrl,
      },
    ).toString()}`;
  };

  const sendHandoffAction = (
    handoffAction: HandoffActionEnum.CONFIRMED | HandoffActionEnum.CANCELLED,
  ) => {
    const conversationId = currentConversation?.conversation_id;
    if (typeof conversationId !== "number" || Number.isNaN(conversationId)) {
      return;
    }
    apiPost<APIResponse>(CONVERSATION_API.handoffAction(), {
      conversation_id: conversationId,
      handoff_action: handoffAction,
    }).catch((error) => {
      console.error("Failed to send handoff action:", error);
      throwCasaApiError(error, "[ChatPage] Failed to send handoff action");
    });
  };

  const handleWhatsApp = ({ withContext }: { withContext: boolean }) => {
    sendHandoffAction(
      withContext ? HandoffActionEnum.CONFIRMED : HandoffActionEnum.CANCELLED,
    );

    const _conversation = `Chat history with AI Casa: ${htmlUrl}`;
    const whatsAppLink = servicesConfig.whatsAppLink;
    const openLink = withContext
      ? `${whatsAppLink}?text=${_conversation}`
      : `${whatsAppLink}`;
    window.open(openLink, "_blank");
  };

  const handleGladlyApp = async () => {
    try {
      const response = await apiGet<
        APIResponse<{ email: string; phone: string; name: string }>
      >(CONVERSATION_API.info());

      if (response.code !== 0) {
        const error = new Error(`Failed to get conversation info`);
        throw error;
      }

      sendHandoffAction(HandoffActionEnum.CONFIRMED);
      const { email, phone, name } = response.data;
      await handoffToGladly({
        user: {
          name: name || email || phone,
          email: email || undefined,
          phoneNumber: email ? undefined : phone || undefined,
        },
        historyText: htmlUrl
          ? `Conversation history with Casa: ${htmlUrl}`
          : "",
        onBeforeOpen: closeChat,
      });
      return;
    } catch (error) {
      console.log("Failed to handoff to Gladly:", error);
      throwCasaApiError(error, "[ChatPage] Failed to handoff to Gladly");
      toast({
        description:
          shouldPromptGladlyRefresh(error)
            ? GLADLY_REFRESH_MESSAGE
            : error instanceof Error
              ? error.message
              : "Handoff to Gladly failed, please refresh the page and try again.",
      });
    }
  };

  const handleGladlyAppCancel = () => {
    sendHandoffAction(HandoffActionEnum.CANCELLED);
    setGladlyVisible((_) => ({ source: null, visible: false }));
  };

  const handleRequestLogin = async (status: "success" | "rejected") => {
    setIsMessageLoading(true);

    const conversationId =
      currentConversation?.conversation_id || EMPTY_CONVERSATION_ID;
    try {
      const response = await apiPost(LOGIN_API.login(), {
        conversation_id: conversationId,
        request_login_status: status,
      });

      if (response.code !== 0) {
        // TODO: 目前如果请求失败是没有重试机制的
        throw new Error(
          response.msg ||
            "An unexpected error has occurred. Please try again later.",
        );
      }
      const aiMessage: MessageData = generateAiMessage(response.data);
      modifyMessageData((prevData) => {
        const list = prevData?.list || [];
        const userMessage = list[list.length - 1];
        const newList = list.map((msg) => {
          if (msg.message_id === userMessage.message_id) {
            return {
              ...msg,
              conversation_id: aiMessage.conversation_id,
              conversation_status: aiMessage.conversation_status,
            };
          }
          return msg;
        });
        return {
          list: [...newList, aiMessage],
          total: prevData?.total || 0,
        };
      });
    } catch (error) {
      toast({
        description: `Login request failed: ${
          error instanceof Error ? error.message : ""
        }`,
      });

      throwCasaApiError(error, "[ChatPage] Failed to request login");
    } finally {
      setIsMessageLoading(false);
    }
  };

  // NOTE: 用户同意登录并且成功后
  const handleResolveLogin = async () => {
    setLoginVisible(false);

    const accessToken = getAccessToken();
    if (!accessToken) {
      setLoginVisible(true);
    } else {
      handleRequestLogin("success");
    }
  };

  // NOTE: 用户拒绝登录
  const handleRejectLogin = async () => {
    setLoginVisible(false);

    handleRequestLogin("rejected");
  };

  // NOTE: 初始化时需要获取用户登录状态去判断是否展示登录弹窗
  // NOTE: 收到信息之后需要判断是否需要展示登录弹窗
  const handleGetLoginStatus = async () => {
    const messages = messageData?.list || [];
    const currentMessage = messages[messages.length - 1];
    if (currentMessage?.message_status === MessageStatusEnum.PROCESSING) {
      handleResolveLogin();
    } else {
      handleRejectLogin();
    }
  };

  // NOTE: 初始化时判断是否有 conversation
  // NOTE: 如果没有，则不需要做任何操作
  // NOTE: 如果有，则判断最新的 conversation 状态是否为 active
  // NOTE: 如果为 active，则需要获取用户登录状态去判断是否展示登录弹窗
  // NOTE: 如果不为 active，则不需要做任何操作
  useEffect(() => {
    if (isCheckedConversation) {
      const messages = messageData?.list || [];
      if (
        currentConversation &&
        isTurnOnConversation(currentConversation.conversation_status) &&
        messages.length > 0 &&
        messages[messages.length - 1].message_status ===
          MessageStatusEnum.PROCESSING &&
        !isLoginOptionalPending.current
      ) {
        handleGetLoginStatus();
      }
    }
  }, [isCheckedConversation]);

  /**
   * 核心消息发送逻辑
   * @param options.content - 消息内容（主动消息时为空字符串）
   * @param options.messageFiles - 要发送的文件列表（主动消息时为空数组）
   * @param options.isProactive - 是否为主动消息（不需要展示用户消息气泡）
   * @param options.isFromOption - 是否来自快速回复选项
   */
  const sendChatMessage = async (params: {
    content: string;
    messageFiles: File[];
    isProactive?: boolean;
    isFromOption?: boolean;
  }) => {
    const {
      content,
      messageFiles,
      isProactive = false,
      isFromOption = false,
    } = params;

    const conversationId =
      currentConversation?.conversation_status ===
        ConversationStatusEnum.ACTIVE ||
      currentConversation?.conversation_status ===
        ConversationStatusEnum.PENDING
        ? currentConversation?.conversation_id
        : EMPTY_CONVERSATION_ID;

    let userMessage: MessageData | null = null;

    // 非主动消息时：生成并添加用户消息
    if (!isProactive) {
      userMessage = generateUserMessage(
        content,
        messageFiles.filter((file) => file.status === FileStatus.SUCCESSED),
        conversationId,
      );
      modifyMessageData((prevData) => {
        const newList = prevData?.list || [];
        return {
          list: [...newList, userMessage as MessageData],
          total: prevData?.total || 0,
        };
      });
    }

    setIsMessageLoading(true);

    try {
      const deviceInfo = getCachedDeviceInfo();
      const chatMessage = {
        client_version: version,
        entry: entryPath,
        market: market,
        current_url: window.location.href,
        conversation_id: currentConversation?.conversation_id || "",
        message: content.trim(),
        files: messageFiles
          .filter((file) => file.status === FileStatus.SUCCESSED)
          .map((file) => ({
            url: file.url,
            name: file.name,
            size: file.size,
            type: file.type,
          })),
        device_info: {
          browser: {
            name: deviceInfo?.browser.name,
            version: deviceInfo?.browser.version,
            ua: deviceInfo?.userAgent,
          },
          network: deviceInfo?.connection?.effectiveType,
          os: deviceInfo?.platform,
          device_type: deviceInfo?.deviceType,
        },
        ...(isFromOption && { is_from_option: true }),
      };

      const response = await apiPost<APIResponse<MessageData>>(
        CHAT_API.message(),
        chatMessage,
      );

      if (response.code !== 0) {
        if (response.code === 13001401) {
          setLoginVisible(true);
          return;
        } else {
          throw new Error(
            response.msg ||
              "An unexpected error has occurred. Please try again later.",
          );
        }
      }

      const _curConversation: Conversation = generateConversation(
        response.data,
      );

      if (
        currentConversation?.conversation_id !==
          _curConversation.conversation_id ||
        currentConversation?.conversation_status !==
          _curConversation.conversation_status
      ) {
        setCurrentConversation(_curConversation);
      }

      // 非主动消息时：更新用户消息的 conversation 信息
      if (!isProactive && userMessage) {
        modifyMessageData((prevData) => {
          const list = prevData?.list || [];
          const newList = list.map((msg) => {
            if (msg.message_id === userMessage.message_id) {
              return {
                ...msg,
                ..._curConversation,
              };
            }
            return msg;
          });
          return {
            list: [...newList],
            total: prevData?.total || 0,
          };
        });
      }

      const aiMessage: MessageData = generateAiMessage(response.data);
      modifyMessageData((prevData) => {
        const newList = prevData?.list || [];
        return {
          list: [...newList, aiMessage],
          total: (prevData?.total || 0) + 1,
        };
      });

      // 非主动消息 且 AI消息处于处理中状态时：获取登录状态
      // TODO: 优化 Login 逻辑每次进来都请求
      // TODO: 优化 CSAT、Handoff 只触发一次
      if (
        !isProactive &&
        aiMessage.message_status === MessageStatusEnum.PROCESSING
      ) {
        handleGetLoginStatus();
      }
    } catch (error) {
      if (error instanceof Error) {
        if (!isProactive) {
          // revert latest message.
          modifyMessageData((prevData) => {
            const newList = prevData?.list || [];
            return {
              list: [...newList.slice(0, -1)],
              total: prevData?.total || 0,
            };
          });
        }
        toast({
          description: `Failed to send message: ${error.message}`,
        });
      }
      // tested!
      throwCasaApiError(error, "[ChatPage] Failed to send message");
    } finally {
      setIsMessageLoading(false);
    }
  };

  /**
   * 处理输入框消息变化
   */
  const handleMessageChange = (_: string, isEmpty: boolean) => {
    setIsInputEmpty(isEmpty);
  };

  /**
   * 处理用户发送消息
   */
  const handleSendMessage = async (content: string, isFromOption?: boolean) => {
    if (!content.trim() && files.length === 0) return;

    const currentFiles = files;
    clearFiles();

    await sendChatMessage({
      content,
      messageFiles: currentFiles,
      isProactive: false,
      isFromOption: isFromOption || false,
    });
  };

  /**
   * 处理主动问候消息（系统自动发送）
   */
  const handleSendProactiveMessage = async () => {
    await sendChatMessage({
      content: "",
      messageFiles: [],
      isProactive: true,
    });
  };

  // NOTE: 提交 CSAT 表单, 跟踪是否提交过 CSAT
  const handleCSATSubmit = (csat: {
    conversationId: number;
    rate: string;
    rateReason?: string;
    advice?: string;
  }) => {
    trackEvent({
      event: TRACK_EVENT,
      category: "csat",
      action: SUBMIT_ACTION,
      tag: BUTTON_TAG,
      tag_value: "csat_submit",
    });
  };

  // NOTE: 返回 FAQ 页面
  const handleBack = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "chat",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "back",
    });

    setCurrentView("faq");
    clearFiles();
  };

  const handleClose = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "chat",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "close",
    });

    closeChat();
  };

  // NOTE: 当 chat rendered 时，需要轮询是否需要展示 WhatsApp 弹窗或 CSAT 表单
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let isMounted = true; // 组件挂载状态标记

    const pollCSATStatus = async () => {
      // 检查组件是否仍然挂载
      if (!isMounted || !isCheckedConversation) {
        return;
      }

      setIsPolling(true);

      try {
        const response = await apiGet<
          APIResponse<{
            conversation_id: number;
            status: string;
            has_reviewed: boolean;
            html_url: string;
            is_business_hours: boolean;
            handoff_action?: unknown;
          }>
        >(CONVERSATION_API.status());

        // 再次检查组件是否仍然挂载（异步请求完成后）
        if (!isMounted) {
          return;
        }

        const {
          conversation_id,
          status,
          has_reviewed,
          html_url,
          is_business_hours,
          handoff_action,
        } = response.data;
        if (
          conversation_id !== currentConversation?.conversation_id ||
          status !== currentConversation?.conversation_status
        ) {
          setCurrentConversation({
            conversation_id: conversation_id,
            conversation_status: status as ConversationStatusEnum,
          });

          if (
            initContext?.conversationId === conversation_id &&
            status !== ConversationStatusEnum.PENDING
          ) {
            fetchLatestMessageForPendingConversation();
            setInitContext(null);
          }
        }

        if (has_reviewed !== hasReviewed) {
          setHasReviewed(has_reviewed);
        }

        if (html_url && html_url !== htmlUrl) {
          setHtmlUrl(html_url);
        }

        if (typeof is_business_hours === "boolean") {
          setIsBusinessHours(is_business_hours);
        }

        if (handoff_action && handoff_action !== handoffAction) {
          setHandoffAction(handoff_action as HandoffActionEnum);
        }
      } catch (error) {
        // 只有在组件仍然挂载时才记录错误
        if (isMounted) {
          console.error("Failed to poll conversation status:", error);
        }

        // FIXME: 这里没有 throw error，出现错误后不会触发降级逻辑
      } finally {
        // 只有在组件仍然挂载时才继续轮询
        if (isMounted && isCheckedConversation) {
          timeoutId = setTimeout(pollCSATStatus, 1000);
        }
      }
    };

    // 初始立即执行一次
    pollCSATStatus();

    return () => {
      isMounted = false; // 标记组件已卸载
      setIsPolling(false);
      clearTimeout(timeoutId);
    };
  }, [isCheckedConversation, currentConversation?.conversation_status]);

  // NOTE: 判断是否需要展示 CSAT 表单
  useEffect(() => {
    if (isPolling) {
      if (
        currentConversation?.conversation_status ===
        ConversationStatusEnum.CLOSED
      ) {
        setLoginVisible(false);

        if (!hasReviewed && isInputEmpty) {
          setCSATVisible(true);
        }
      }
    }
  }, [
    currentConversation?.conversation_id,
    currentConversation?.conversation_status,
    hasReviewed,
    isBusinessHours,
  ]);

  // NOTE: 判断是否需要展示 WhatsApp 弹窗
  useEffect(() => {
    if (isPolling) {
      if (
        currentConversation?.conversation_status ===
          ConversationStatusEnum.HANDOFF &&
        handoffAction === HandoffActionEnum.NONE
      ) {
        if (market === Market.US) {
          setGladlyVisible({
            source: "conversation",
            visible: true,
          });
        } else if (typeof isBusinessHours === "boolean" && isBusinessHours) {
          setWhatsAppVisible({
            source: "conversation",
            visible: true,
          });
        }
      }
    }
  }, [
    currentConversation?.conversation_id,
    currentConversation?.conversation_status,
    handoffAction,
  ]);

  return (
    <ChatProvider value={{ onMessageChange }}>
      <ChatFramework
        ref={chatFrameworkRef}
        // 无限滚动容器
        contentRef={chatFrameworkContentRef}
        header={
          <span className="text-foreground text-[13px]">Let's chat!</span>
        }
        headerShadow={true}
        headerBorder={true}
        footer={
          <div className="flex items-center justify-center pt-3 pl-4 pr-4 pb-[30px] min-h-[90px]">
            <InputBar
              isLoading={isMessageLoading}
              onSendMessage={handleSendMessage}
              onMessageChange={handleMessageChange}
            />
          </div>
        }
        footerContent={
          <>
            {files.length > 0 && (
              <div
                className="py-2 px-4"
                style={{ boxShadow: "0px -2px 4px 0px rgba(0, 0, 0, 0.04)" }}
              >
                {/* 文件上传结果区域 */}
                <div className="flex items-center space-x-2">
                  {files?.map((file) => (
                    <UploadedFile
                      key={file.id}
                      file={file}
                      onDelete={delFile}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        }
        onBack={handleBack}
        onClose={handleClose}
      >
        <div className="flex flex-col h-full">
          {/* 消息列表 */}
          <div className="flex-1">
            <div className="space-y-6">
              <UserTip />

              {/* 加载更多历史消息指示器 */}
              {(firstLoading || loadingMore) && <LoadingMoreHistory />}

              {/* 消息列表 */}
              {conversationList.length > 0 &&
                conversationList.map((conv, convIndex) => {
                  return (
                    <div key={conv.conversation_id}>
                      {convIndex !== 0 && (
                        <>
                          <ConversationDivider
                            info={messageDateFormatter(
                              conv.messages[0].created_at,
                            )}
                          />
                          <UserTip />
                        </>
                      )}

                      {conv.messages.map((message, i) => {
                        const isLatestMessage =
                          convIndex === conversationList.length - 1 &&
                          i === conv.messages.length - 1;
                        return (
                          <Message
                            key={message.message_id}
                            message={message}
                            isLatestMessage={isLatestMessage}
                            onSendMessage={handleSendMessage}
                          />
                        );
                      })}

                      {!isMessageLoading &&
                        convIndex === conversationList.length - 1 &&
                        isTurnOffConversation(conv.conversation_status) && (
                          <ConversationDivider info="This chat session is now closed." />
                        )}
                    </div>
                  );
                })}

              {/* 加载中消息 */}
              {isMessageLoading && <LoadingMessage />}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* About Casa */}
          {isCheckedConversation && messageData?.list?.length === 0 && (
            <NiceToMeetYou onSendMessage={handleSendMessage} />
          )}
        </div>

        {/* 登录弹窗 */}
        <Login
          open={loginVisible}
          onOpenChange={setLoginVisible}
          onLogin={handleLogin}
          onCancel={handleRejectLogin}
          containerRef={chatFrameworkRef}
        />

        {/* CSAT 表单 */}
        <CSAT
          open={CSATVisible}
          onOpenChange={setCSATVisible}
          onSubmit={handleCSATSubmit}
          onCancel={() => setCSATVisible(false)}
          containerRef={chatFrameworkRef}
          conversation={currentConversation as Conversation}
        />

        {/* WhatsApp 弹窗 */}
        <WhatsApp
          open={whatsAppVisible.visible}
          onOpenChange={(visible) => {
            if (visible === false) {
              setWhatsAppVisible((_) => ({ source: null, visible: false }));
            } else {
              setWhatsAppVisible((prev) => ({ ...prev, visible: true }));
            }
          }}
          onConfirm={() => handleWhatsApp({ withContext: true })}
          onCancel={() => handleWhatsApp({ withContext: false })}
          onClose={() => sendHandoffAction(HandoffActionEnum.CANCELLED)}
          containerRef={chatFrameworkRef}
        />

        {/* Gladly 移交确认弹窗 */}
        <GladlyApp
          open={gladlyVisible.visible}
          onOpenChange={(visible) => {
            if (visible === false) {
              setGladlyVisible((_) => ({ source: null, visible: false }));
            } else {
              setGladlyVisible((prev) => ({ ...prev, visible: true }));
            }
          }}
          onConfirm={handleGladlyApp}
          onCancel={handleGladlyAppCancel}
          onClose={() => sendHandoffAction(HandoffActionEnum.CANCELLED)}
          containerRef={chatFrameworkRef}
        />

        <DislikeReason
          open={dislikeReasonPanelInfo.visible}
          isVisibleContactSupport={
            currentConversation?.conversation_status &&
            isTurnOnConversation(currentConversation?.conversation_status)
          }
          onOpenChange={(open) =>
            setDislikeReasonPanelInfo((prev) => ({ ...prev, visible: open }))
          }
          onSubmit={async (reason: string) => {
            const response = await apiPost(CHAT_API.messageReview(), {
              message_id: dislikeReasonPanelInfo.messageId,
              is_like: false,
              reason,
            });
            if (response.code !== 0) {
              toast({
                description: `Submit dislike reason failed: ${response.msg}`,
              });
              return;
            }
            toast({
              description: FeedbackSentence,
            });
          }}
          onCancel={() =>
            setDislikeReasonPanelInfo((prev) => ({ ...prev, visible: false }))
          }
          onContactSupport={async () => {
            const response = await apiPost<APIResponse>(CHAT_API.handoff(), {
              conversation_id: currentConversation?.conversation_id || "",
            });

            if (response.code !== 0) {
              toast({
                description: `Failed to initiate handoff: ${response.msg}`,
              });
              return;
            }
            if (market === Market.US) {
              setGladlyVisible({ source: "message", visible: true });
            } else {
              setWhatsAppVisible({ source: "message", visible: true });
            }
          }}
          containerRef={chatFrameworkRef}
        />
      </ChatFramework>
    </ChatProvider>
  );
};
